import React from 'react';
import { useDropzone } from 'react-dropzone';
import { Controller, useFormContext } from 'react-hook-form';
import { alpha } from '@mui/material/styles';
import { Box, FormHelperText, IconButton, Stack, Typography } from '@mui/material';
import { IconWrapper } from 'components/Icon';

const MultiUploadInput = ({ files = [], onChange, error = false, helperText = null, disabled = false, sx = {}, ...other }) => {
  const { getRootProps, getInputProps, isDragActive, isDragReject } = useDropzone({
    disabled,
    multiple: true,
    onDrop: (acceptedFiles) => onChange([...files, ...acceptedFiles]),
    ...other,
  });

  const hasError = isDragReject || !!error;

  const handleRemove = (index) => onChange(files.filter((_, i) => i !== index));

  return (
    <Box sx={{ width: 1, position: 'relative', ...sx }}>
      <Box
        {...getRootProps()}
        sx={{
          p: 5,
          outline: 'none',
          borderRadius: 1,
          cursor: 'pointer',
          overflow: 'hidden',
          position: 'relative',
          border: (theme) => `1px solid ${alpha(theme.palette.grey[500], 0.2)}`,
          transition: (theme) => theme.transitions.create(['opacity', 'padding']),
          '&:hover': {
            opacity: 0.72,
          },
          ...(isDragActive && {
            opacity: 0.72,
          }),
          ...(disabled && {
            opacity: 0.48,
            pointerEvents: 'none',
          }),
          ...(hasError && {
            color: 'error.main',
            bgcolor: 'error.lighter',
            borderColor: 'error.light',
          }),
        }}
      >
        <input {...getInputProps()} />
        <Stack spacing={1} sx={{ textAlign: 'center' }}>
          <Typography variant="h6">Drop or Select files</Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Drop files here or click
            <Box component="span" sx={{ mx: 0.5, color: 'primary.main', textDecoration: 'underline' }}>
              browse
            </Box>
            thorough your machine
          </Typography>
        </Stack>
      </Box>

      {files.length > 0 && (
        <Stack mt={1} gap={0.5}>
          {files.map((file, index) => (
            <Stack key={`${file.name}-${index}`} direction="row" alignItems="center" justifyContent="space-between">
              <Typography variant="body2" sx={{ color: 'primary.main', wordBreak: 'break-all' }}>
                {file.name}
              </Typography>
              <IconButton size="small" aria-label={`Remove ${file.name}`} onClick={() => handleRemove(index)}>
                <IconWrapper icon="xMark" sx={{ svg: { width: 16, height: 16 } }} />
              </IconButton>
            </Stack>
          ))}
        </Stack>
      )}

      <Box ml={-2}>{helperText && helperText}</Box>
    </Box>
  );
};

export const MultiUpload = ({ name, helperText = '', ...other }) => {
  const { control } = useFormContext();

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => (
        <MultiUploadInput
          files={field.value || []}
          onChange={field.onChange}
          error={!!error}
          helperText={
            (!!error || helperText) && (
              <FormHelperText error={!!error} sx={{ px: 2 }}>
                {error ? error?.message : helperText}
              </FormHelperText>
            )
          }
          {...other}
        />
      )}
    />
  );
};
